import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ExportButton } from '../../components/ExportButton';
import { formatInstantDate } from '../../lib/format';
import { STATUS_LABEL, statusTone } from '../../lib/pipeline';
import { platformLabel } from '../../lib/platforms';
import { RENDERED_SLIDES_BUCKET } from '../../lib/storage';
import { domainOf, safeExternalUrl } from '../../lib/url';
import type { GenerationRequestSummaryRead, PostType } from '../../types/api';
import { useSignedAssetUrl } from '../knowledge-base/useSignedAssetUrl';
import { POST_TYPE_GLYPH, POST_TYPE_LABEL, postTitle, tryLabel } from './postLabels';
import { TileMenu } from './TileMenu';

interface PostPictureProps {
  /** Object key in `rendered-slides`, or null/absent when nothing rendered. */
  path: string | null | undefined;
  className?: string;
  /** What to draw when there is no picture, or it could not be signed. */
  fallback: ReactNode;
}

/**
 * The first rendered slide of a run, signed out of the private
 * `rendered-slides` bucket, else the fallback.
 *
 * Only a real picture carries `.post-picture`, which is on the light-island
 * list in `index.css`: a rendered slide is content and stays light in dark
 * mode, while the caption-card fallback is chrome and follows the theme.
 * Decorative (`alt=""`): the link beside it names the post.
 */
export function PostPicture({ path, className = '', fallback }: PostPictureProps) {
  const { url, error } = useSignedAssetUrl(path ?? null, RENDERED_SLIDES_BUCKET);
  if (!path || error) return <span className={className}>{fallback}</span>;
  return (
    <span className={`${className} post-picture`.trim()}>
      {url ? <img className="post-picture__image" src={url} alt="" loading="lazy" /> : null}
    </span>
  );
}

/** The glyph a row shows when a post has no picture: its shape, in mono. */
export function PostTypeGlyph({ postType }: { postType: PostType }) {
  return (
    <span className="post-glyph" aria-hidden="true">
      {POST_TYPE_GLYPH[postType]}
    </span>
  );
}

interface PostTileProps {
  request: GenerationRequestSummaryRead;
  businessId: string;
  /**
   * `compact` — Home's Recent posts: the square, the status in its corner, the
   * platform and the date. `full` — History: the same square plus the words,
   * provenance chips, the attempt, and the overflow menu.
   */
  variant?: 'compact' | 'full';
}

/**
 * One generation request as a picture-first tile: the rendered slide when
 * there is one, else the caption on paper (a text-only or copy-only run), with
 * the status stamped in the corner. Shared by Home and History so a post looks
 * the same wherever it is recognised.
 *
 * The whole tile opens `/review/{id}` through a stretched link, so the source
 * chip and the overflow menu stay real controls beside a real link rather than
 * nested inside one.
 */
export function PostTile({ request, businessId, variant = 'full' }: PostTileProps) {
  const tone = statusTone(request.status);
  const status = STATUS_LABEL[request.status];
  const platform = platformLabel(request.platform);
  const date = formatInstantDate(request.created_at);
  const title = postTitle(request);
  const isFull = variant === 'full';
  // The article this run was grounded on, when it came off the newsfeed.
  const sourceHref = safeExternalUrl(request.source_url);

  const fallback = (
    <span className="post-tile__card">
      {request.caption_preview ? (
        <span className="post-tile__card-text">{request.caption_preview}</span>
      ) : (
        <PostTypeGlyph postType={request.post_type} />
      )}
    </span>
  );

  return (
    <li className={`post-tile post-tile--${variant}`}>
      <div className="post-tile__media">
        <PostPicture
          path={request.thumbnail_path}
          className="post-tile__picture"
          fallback={fallback}
        />
        <span className={`status-chip status-chip--${tone} post-tile__status`}>{status}</span>
      </div>

      <div className="post-tile__body">
        {isFull ? (
          <h2 className="post-tile__title">
            <Link className="post-tile__link" to={`/review/${request.id}`}>
              {title}
            </Link>
          </h2>
        ) : null}

        <p className="post-tile__meta">
          {isFull ? (
            <span>{platform}</span>
          ) : (
            <Link
              className="post-tile__link"
              to={`/review/${request.id}`}
              aria-label={`${title}. ${status}, ${platform}, ${date}.`}
            >
              {platform}
            </Link>
          )}
          <span aria-hidden={isFull ? undefined : true}> · {date}</span>
          {isFull ? (
            <>
              <span> · {POST_TYPE_LABEL[request.post_type]}</span>
              {request.attempt > 1 ? <span> · {tryLabel(request.attempt)}</span> : null}
            </>
          ) : null}
        </p>

        {isFull && (sourceHref || request.planned_post_id) ? (
          <p className="post-tile__chips">
            {sourceHref ? (
              <a
                className="source-chip post-tile__raised"
                href={sourceHref}
                target="_blank"
                rel="noopener noreferrer"
              >
                from {domainOf(sourceHref) ?? sourceHref}
              </a>
            ) : null}
            {request.planned_post_id ? <span className="intent-tag">from calendar</span> : null}
          </p>
        ) : null}
      </div>

      {isFull && request.status === 'complete' ? (
        <TileMenu label={`More for ${title}`}>
          <ExportButton
            businessId={businessId}
            requestId={request.id}
            platform={request.platform}
            label="Download"
          />
        </TileMenu>
      ) : null}
    </li>
  );
}
